import { Injectable, inject } from '@angular/core';
import { SnowplowService } from '@sirius/common';
import {
    Cart,
    CheckoutStep,
    createCart,
    createCheckoutStep,
    createOrder,
    createProduct,
    createTransactionError,
    Order,
    Product,
    trackAddToCartSpec,
    trackCheckoutStepSpec,
    trackLoginSpec,
    trackLogoutSpec,
    trackProductListClickSpec,
    trackProductListViewSpec,
    trackProductViewSpec,
    trackRemoveFromCartSpec,
    trackTransactionErrorSpec,
    trackTransactionSpec,
    TransactionError,
} from '@urweb/snowtype';

interface CartAction {
    cart: Cart;
    product: Product[];
}
interface CheckoutStepAction {
    cart: Cart;
    checkout_step: CheckoutStep;
}
interface ProductAction {
    product: Product;
}
interface ProductListViewAction {
    products: Product[];
}
interface Transaction {
    cart: Cart;
    products: Product[];
    order: Order;
}
interface TransactionErrorAction {
    transactionError: TransactionError;
    order?: Order;
    cart: Cart;
}

@Injectable({
    providedIn: 'root',
})
export class TrackingCommerceSnowplowService {
    private _snowplowService = inject(SnowplowService);



    public trackLogout() {
        this._snowplowService.track({
            fn: (data) => {
                return trackLogoutSpec({
                    event_type: 'logout',
                    context: data,
                    timestamp: new Date().getTime(),
                });
            },
            data: []
        });
    }

    public trackAddToCart({ product, cart }: CartAction) {
        const data = [];

        data.push(createCart(cart));

        for (let productIndex = 0; productIndex < product.length; productIndex++) {
            const productData = createProduct(product[productIndex]);
            data.push(productData);
        }

        this._snowplowService.track({
            fn: (data) =>
                trackAddToCartSpec({
                    type: 'add_to_cart',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data: data,
        });
    }

    public removeFromCart({ product, cart }: CartAction) {
        const data = [];
        data.push(createCart(cart));

        for (let productIndex = 0; productIndex < product.length; productIndex++) {
            const productData = createProduct(product[productIndex]);
            data.push(productData);
        }

        this._snowplowService.track({
            fn: (data) =>
                trackRemoveFromCartSpec({
                    type: 'remove_from_cart',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data: data,
        });
    }

    public trackCheckoutStep({ cart, checkout_step }: CheckoutStepAction) {
        const data = [];

        data.push(createCart(cart));
        data.push(createCheckoutStep(checkout_step));

        this._snowplowService.track({
            fn: (data) =>
                trackCheckoutStepSpec({
                    type: 'checkout_step',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data,
        });
    }

    public trackProductListClick({ product }: ProductAction) {
        const data = [];
        data.push(createProduct(product));

        this._snowplowService.track({
            fn: (data) =>
                trackProductListClickSpec({
                    type: 'list_click',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data,
        });
    }

    public trackProductListView({ products }: ProductListViewAction) {
        const data = [];

        for (let productIndex = 0; productIndex < products.length; productIndex++) {
            data.push(createProduct(products[productIndex]));
        }

        this._snowplowService.track({
            fn: (data) =>
                trackProductListViewSpec({
                    type: 'list_view',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data,
        });
    }

    public trackProductView({ product }: ProductAction) {
        const data = [];
        data.push(createProduct(product));

        this._snowplowService.track({
            fn: (data) =>
                trackProductViewSpec({
                    type: 'product_view',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data,
        });
    }

    public trackTransaction({ products, cart, order }: Transaction) {
        const data = [];

        if (products) {
            for (let productIndex = 0; productIndex < products.length; productIndex++) {
                data.push(createProduct(products[productIndex]));
            }
        }

        data.push(createCart(cart));
        data.push(createOrder(order));

        this._snowplowService.track({
            fn: (data) =>
                trackTransactionSpec({
                    type: 'transaction',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data,
        });
    }

    public trackTransactionError({ transactionError, cart, order }: TransactionErrorAction) {
        const data = [];

        data.push(createCart(cart));
        data.push(createTransactionError(transactionError));

        if (order) {
            data.push(createOrder(order));
        }

        this._snowplowService.track({
            fn: (data) =>
                trackTransactionErrorSpec({
                    type: 'trns_error',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data,
        });
    }
}
